import { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Stack
} from '@mui/material';
import TimerIcon from '@mui/icons-material/Timer';
import LocationOnIcon from '@mui/icons-material/LocationOn';

import { useGeolocation } from '../hooks/useGeolocation';
import { visitaService } from '../services/visita.service';
import type { Visita } from '../types/visita.types';

// AIDEV-NOTE: Modal de check-out com contador de duracao em tempo real

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  visita: Visita;
}

function formatDuration(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}h ${minutes.toString().padStart(2, '0')}min ${seconds.toString().padStart(2, '0')}s`;
  }
  return `${minutes}min ${seconds.toString().padStart(2, '0')}s`;
}

export function CheckoutModal({ open, onClose, onSuccess, visita }: Props) {
  const { position, loading: gpsLoading } = useGeolocation(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [elapsed, setElapsed] = useState('');
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // AIDEV-NOTE: Contador animado — atualiza a cada segundo mostrando duracao em tempo real
  useEffect(() => {
    if (!open) return;

    const checkinTime = new Date(visita.checkin_at).getTime();

    const update = () => {
      const now = Date.now();
      setElapsed(formatDuration(now - checkinTime));
    };

    update();
    intervalRef.current = setInterval(update, 1000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [open, visita.checkin_at]);

  const handleCheckout = async () => {
    if (!position) return;

    setSubmitting(true);
    setSubmitError(null);

    try {
      await visitaService.checkout({
        visita_id: visita.id,
        lat: position.lat,
        lng: position.lng,
        accuracy: position.accuracy
      });
      onSuccess();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Erro ao realizar check-out');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Check-out</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {/* Duracao da visita */}
          <Box
            sx={{
              textAlign: 'center',
              p: 2,
              bgcolor: 'primary.50',
              borderRadius: 2
            }}
          >
            <TimerIcon sx={{ fontSize: 32, color: 'primary.main', mb: 0.5 }} />
            <Typography variant="h5" fontWeight="bold" color="primary.main">
              {elapsed}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Duracao da visita
            </Typography>
          </Box>

          {/* Posicao GPS atual */}
          {gpsLoading ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                Obtendo posicao GPS...
              </Typography>
            </Stack>
          ) : position ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <LocationOnIcon sx={{ color: 'success.main', fontSize: 18 }} />
              <Typography variant="body2" color="text.secondary">
                Posicao: {position.lat.toFixed(6)}, {position.lng.toFixed(6)} (~
                {Math.round(position.accuracy)}m)
              </Typography>
            </Stack>
          ) : null}

          {/* Erro de submit */}
          {submitError && <Alert severity="error">{submitError}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancelar
        </Button>
        <Button
          onClick={handleCheckout}
          variant="contained"
          color="primary"
          disabled={!position || submitting}
          startIcon={submitting ? <CircularProgress size={16} /> : <TimerIcon />}
        >
          {submitting ? 'Registrando...' : 'Confirmar Check-out'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
